import { Component, ElementRef, Input, OnChanges, OnDestroy, ViewEncapsulation } from '@angular/core';

// Módulos concretos en lugar del barrel: ver la nota en chart-elements.ts.
// `table` se importa por su efecto: al cargarse registra el elemento Table,
// que card-elements no trae.
import { AdaptiveCard, OpenUrlAction } from 'adaptivecards/lib/card-elements';
import { HostConfig } from 'adaptivecards/lib/host-config';
import 'adaptivecards/lib/table';

import ApexCharts from 'apexcharts';

import { TranslateService } from '@ngx-translate/core';

import { CHART_ELEMENT_TYPES, CHART_SLOT_CLASS, ChartSlot, registerChartElements } from './chart-elements';
import { buildChartOptions, ChartPalette } from './chart-options';

/**
 * Pinta en el chat una Adaptive Card de las que devuelve el agente.
 *
 * La estructura de la tarjeta la resuelve el renderer oficial de Adaptive
 * Cards; los elementos de gráfico, que en Teams los dibuja el host y acá no
 * existen, se montan con ApexCharts sobre los huecos que deja el renderer.
 *
 * Todo el árbol se construye a mano en lugar de con una plantilla: el renderer
 * devuelve nodos del DOM, no vistas de Angular, así que no hay nada que Angular
 * pueda gobernar más allá del contenedor.
 */
@Component({
  selector: 'app-chat-adaptive-card',
  template: '',
  styleUrls: ['./chat-adaptive-card.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class ChatAdaptiveCardComponent implements OnChanges, OnDestroy {

  /** Tarjeta a pintar, tal como llegó por el stream */
  @Input() cardJson: string;

  /** Instancias de ApexCharts montadas, hay que destruirlas a mano */
  private charts: ApexCharts[] = [];

  /** Evita montar gráficos si el componente se fue antes del frame siguiente */
  private destroyed = false;

  /**
   * Constructor
   * @param {ElementRef<HTMLElement>} _host
   * @param {TranslateService} _translateService
   */
  constructor(private _host: ElementRef<HTMLElement>,
    private _translateService: TranslateService) {
    registerChartElements();
  }

  /**
   * OnChanges
   */
  ngOnChanges(): void {
    this.render();
  }

  /**
   * OnDestroy
   */
  ngOnDestroy(): void {
    this.destroyed = true;
    this.destroyCharts();
  }

  /**
   * Parsea la tarjeta, la renderiza y monta los gráficos que traiga.
   */
  private render(): void {
    const host = this._host.nativeElement;

    this.destroyCharts();
    host.innerHTML = '';

    if (!this.cardJson) return;

    let payload: any;

    try {
      payload = typeof this.cardJson === 'string' ? JSON.parse(this.cardJson) : this.cardJson;
    } catch {
      this.renderFallback(host);
      return;
    }

    // El agente puede mandar la tarjeta sola o envuelta en un attachment,
    // que es como viaja hacia Teams
    const cardPayload = payload?.content ?? payload?.card ?? payload;

    try {
      const card = new AdaptiveCard();

      card.hostConfig = this.buildHostConfig();

      // Sin host de Teams, lo único que se puede honrar es abrir enlaces
      card.onExecuteAction = action => {
        if (action instanceof OpenUrlAction && action.url) {
          window.open(action.url, '_blank', 'noopener');
        }
      };

      card.parse(cardPayload);

      const rendered = card.render();

      if (!rendered) {
        this.renderFallback(host);
        return;
      }

      host.appendChild(rendered);

      // Los gráficos se montan en el frame siguiente: ApexCharts mide el
      // contenedor al construirse, y recién después de este appendChild el
      // navegador le puede dar un ancho
      requestAnimationFrame(() => this.mountCharts(rendered, cardPayload));
    } catch (error) {
      console.warn('[ChatAdaptiveCard] No se pudo renderizar la tarjeta:', error);
      this.renderFallback(host);
    }
  }

  /**
   * Monta un gráfico de ApexCharts sobre cada hueco que dejó el renderer.
   *
   * @param {HTMLElement} root Tarjeta ya renderizada y colgada del documento
   * @param {any} cardPayload Tarjeta cruda, para poder avisar qué no se entendió
   */
  private mountCharts(root: HTMLElement, cardPayload: any): void {
    if (this.destroyed) return;

    const slots = Array.from(root.querySelectorAll<ChartSlot>(`.${CHART_SLOT_CLASS}`));
    const palette = this.buildPalette();

    // Un `Chart.*` que no esté registrado lo descarta el renderer sin dejar
    // rastro, así que se avisa acá antes de que el usuario vea un hueco
    this.warnUnsupportedCharts(cardPayload, slots.length);

    slots.forEach(slot => {
      const definition = slot.chartDefinition;
      const options = buildChartOptions(definition, palette);

      // Sin datos no hay nada que dibujar: se muestra el json en su lugar
      if (!options) {
        console.warn('[ChatAdaptiveCard] El gráfico no trae datos utilizables:', definition);
        this.replaceWithRaw(slot, definition);
        return;
      }

      try {
        const chart = new ApexCharts(slot, options);
        this.charts.push(chart);

        // render() es asincrónico: sin este catch, un fallo de ApexCharts se
        // pierde como promesa rechazada y el hueco queda en blanco
        Promise.resolve(chart.render()).catch(error => {
          console.warn('[ChatAdaptiveCard] ApexCharts no pudo dibujar el gráfico:', error, { definition, options });
          this.replaceWithRaw(slot, definition);
        });
      } catch (error) {
        console.warn('[ChatAdaptiveCard] No se pudo construir el gráfico:', error, { definition, options });
        this.replaceWithRaw(slot, definition);
      }
    });
  }

  /**
   * Avisa por consola si la tarjeta trae elementos de gráfico que no están
   * registrados. El renderer los descarta en silencio, así que sin este aviso
   * la única señal sería un hueco donde debería haber un gráfico.
   *
   * @param {any} cardPayload Tarjeta cruda
   * @param {number} mounted Cantidad de huecos que sí dejó el renderer
   */
  private warnUnsupportedCharts(cardPayload: any, mounted: number): void {
    const found = new Set<string>();

    const walk = (node: any): void => {
      if (Array.isArray(node)) {
        node.forEach(walk);
        return;
      }

      if (!node || typeof node !== 'object') return;

      if (typeof node.type === 'string' && node.type.startsWith('Chart.')) {
        found.add(node.type);
      }

      Object.values(node).forEach(walk);
    };

    walk(cardPayload);

    const unsupported = [...found].filter(type => !CHART_ELEMENT_TYPES.includes(type));

    if (unsupported.length > 0) {
      console.warn(
        `[ChatAdaptiveCard] Elementos de gráfico no soportados: ${unsupported.join(', ')}. ` +
        `Hay que agregarlos a CHART_ELEMENT_TYPES en chart-elements.ts.`
      );
    }

    if (found.size > 0 && mounted === 0) {
      console.warn('[ChatAdaptiveCard] La tarjeta declara gráficos pero el renderer no dejó ningún hueco:', [...found]);
    }
  }

  /**
   * Cambia el hueco de un gráfico por su json, para que el dato siga estando
   * a mano cuando el dibujo no salió.
   *
   * @param {ChartSlot} slot Hueco que dejó el renderer
   * @param {Record<string, any>} definition Definición cruda del gráfico
   */
  private replaceWithRaw(slot: ChartSlot, definition: Record<string, any>): void {
    const details = document.createElement('details');
    details.className = 'bl-ac-raw';

    const summary = document.createElement('summary');
    summary.textContent = this.label('No se pudo dibujar el gráfico · ver datos', 'Could not draw the chart · view data');

    const pre = document.createElement('pre');
    pre.textContent = JSON.stringify(definition, null, 2);

    details.appendChild(summary);
    details.appendChild(pre);

    slot.replaceWith(details);
  }

  /**
   * Deja un aviso en lugar de la tarjeta, con el json a la vista para poder
   * diagnosticar qué mandó el agente.
   *
   * @param {HTMLElement} host Contenedor del componente
   */
  private renderFallback(host: HTMLElement): void {
    const details = document.createElement('details');
    details.className = 'bl-ac-raw';

    const summary = document.createElement('summary');
    summary.textContent = this.label('No se pudo mostrar el gráfico · ver datos', 'Could not display the chart · view data');

    const pre = document.createElement('pre');
    pre.textContent = this.cardJson;

    details.appendChild(summary);
    details.appendChild(pre);
    host.appendChild(details);
  }

  /**
   * Traduce la tarjeta al lenguaje visual del proyecto: tipografía heredada,
   * fondo transparente y los colores de la paleta de B.R.A.I.N.
   *
   * @returns {HostConfig} Devuelve la configuración del host
   */
  private buildHostConfig(): HostConfig {
    const palette = this.buildPalette();

    const foregroundColors = {
      default: { default: palette.text, subtle: palette.muted },
      accent: { default: palette.colors[0], subtle: palette.colors[0] },
      good: { default: '#28c76f', subtle: '#28c76f' },
      warning: { default: '#ff9f43', subtle: '#ff9f43' },
      attention: { default: '#ea5455', subtle: '#ea5455' },
    };

    return new HostConfig({
      fontFamily: 'inherit',
      spacing: { small: 4, default: 8, medium: 14, large: 18, extraLarge: 24, padding: 0 },
      separator: { lineThickness: 1, lineColor: palette.border },
      containerStyles: {
        default: { backgroundColor: 'transparent', foregroundColors },
        emphasis: { backgroundColor: palette.surface, foregroundColors },
        accent: { backgroundColor: 'transparent', foregroundColors },
        good: { backgroundColor: 'transparent', foregroundColors },
        warning: { backgroundColor: 'transparent', foregroundColors },
        attention: { backgroundColor: 'transparent', foregroundColors },
      },
      actions: {
        actionAlignment: 'left',
        actionsOrientation: 'horizontal',
        buttonSpacing: 8,
        maxActions: 5,
        spacing: 'default',
        showCard: { actionMode: 'inline', inlineTopMargin: 8, style: 'emphasis' },
      },
      supportsInteractivity: true,
    });
  }

  /**
   * Resuelve los colores del tema activo leyendo los tokens css del proyecto,
   * para que la tarjeta acompañe el modo claro y el oscuro sin duplicar nada.
   *
   * @returns {ChartPalette} Devuelve la paleta con la que se pintan los gráficos
   */
  private buildPalette(): ChartPalette {
    const styles = getComputedStyle(this._host.nativeElement);

    const token = (name: string, fallback: string): string => {
      const value = styles.getPropertyValue(name).trim();

      return value || fallback;
    };

    const primary = token('--brain-primary', '#0f8084');

    return {
      // La primera es la de la marca; el resto acompaña con suficiente
      // contraste entre sí para poder distinguir series
      colors: [primary, '#7367f0', '#ff9f43', '#28c76f', '#ea5455', '#00cfe8', '#a8aaae'],
      text: token('--text-base', '#5e5873'),
      muted: token('--text-muted', '#6e6b7b'),
      border: token('--border-base', '#ebe9f1'),
      surface: token('--surface-muted', '#f2f2f2'),
      dark: document.body.classList.contains('dark-layout'),
    };
  }

  /**
   * Destruye los gráficos montados. ApexCharts deja listeners de resize
   * colgados si no se le avisa.
   */
  private destroyCharts(): void {
    this.charts.forEach(chart => {
      try {
        chart.destroy();
      } catch {
        // Si el nodo ya se fue del DOM, destruir puede fallar y da igual
      }
    });

    this.charts = [];
  }

  /**
   * Etiqueta localizada sin depender de claves nuevas del backend de traducciones.
   * @param {string} es Texto en español
   * @param {string} en Texto en inglés
   * @returns {string} Texto según el idioma activo
   */
  private label(es: string, en: string): string {
    return this._translateService.currentLang === 'en' ? en : es;
  }
}
